import { lazy } from 'react'
import type { RouteObject } from 'react-router-dom'
import { ROUTES, ROLES } from '@/app/config'
import { AuthGuard, PermissionGuard } from './guards'

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'))
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'))
const DeliveriesListPage = lazy(() => import('@/features/deliveries/pages/DeliveriesListPage'))
const DeliveryDetailPage = lazy(() => import('@/features/deliveries/pages/DeliveryDetailPage'))
const DriverSummaryPage = lazy(() => import('@/features/deliveries/pages/DriverSummaryPage'))
const ReturnsListPage = lazy(() => import('@/features/returns/pages/ReturnsListPage'))
const ReturnDetailPage = lazy(() => import('@/features/returns/pages/ReturnDetailPage'))
const WarehouseEventsPage = lazy(() => import('@/features/warehouse/pages/WarehouseEventsPage'))
const ReturnReasonSummaryPage = lazy(
  () => import('@/features/returns/pages/ReturnReasonSummaryPage')
)
const CreateReturnFromErpSalePage = lazy(
  () => import('@/features/returns/pages/CreateReturnFromErpSalePage')
)
const VerificationListPage = lazy(
  () => import('@/features/returns/pages/VerificationListPage')
)
const VerificationDetailPage = lazy(
  () => import('@/features/returns/pages/VerificationDetailPage')
)
const ReturnErpSettingsPage = lazy(
  () => import('@/features/returns/pages/ReturnErpSettingsPage')
)
const MapSettingsPage = lazy(() => import('@/features/map-config/pages/MapSettingsPage'))
const DeliveryReasonsPage = lazy(
  () => import('@/features/delivery-reasons/pages/DeliveryReasonsPage')
)
const UsersListPage = lazy(() => import('@/features/users/pages/UsersListPage'))
const UserDetailPage = lazy(() => import('@/features/users/pages/UserDetailPage'))
const ErpProductsPage = lazy(() => import('@/features/erp/pages/ErpProductsPage'))
const ErpSalesPage = lazy(() => import('@/features/erp/pages/ErpSalesPage'))
const ErpLoadsListPage = lazy(() => import('@/features/erp/loads/pages/ErpLoadsListPage'))
const ErpLoadDetailPage = lazy(() => import('@/features/erp/loads/pages/ErpLoadDetailPage'))
const DeliveryMapPage = lazy(() => import('@/features/delivery-map/pages/DeliveryMapPage'))
const VehiclesPage = lazy(() => import('@/features/vehicles/pages/VehiclesPage'))
const CustomersPage = lazy(() => import('@/features/placeholders/pages/CustomersPage'))
const ErpSyncPage = lazy(() => import('@/features/placeholders/pages/ErpSyncPage'))
const ErpInventoryPage = lazy(() => import('@/features/placeholders/pages/ErpInventoryPage'))
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage'))
const NotReturnedReportPage = lazy(() => import('@/features/reports/pages/NotReturnedReportPage'))
const DriverProfilePage = lazy(() => import('@/features/driver-app/pages/DriverProfilePage'))
const DriverNotificationsPage = lazy(
  () => import('@/features/driver-app/pages/DriverNotificationsPage')
)
const MyLoadsPage = lazy(() => import('@/features/driver-app/pages/MyLoadsPage'))
const LoadDetailPage = lazy(() => import('@/features/driver-app/pages/LoadDetailPage'))
const StopActionPage = lazy(() => import('@/features/driver-app/pages/StopActionPage'))
const DriverRoutePage = lazy(() => import('@/features/driver-route/pages/DriverRoutePage'))
const DriverPendingSyncPage = lazy(
  () => import('@/features/driver-app/pages/DriverPendingSyncPage')
)
const NotFoundPage = lazy(() => import('@/shared/components/feedback/NotFoundPage'))

// Layout components
const MainLayout = lazy(() => import('@/shared/components/layout/MainLayout'))
const DriverLayout = lazy(() => import('@/shared/components/layout/DriverLayout'))

export const routes: RouteObject[] = [
  // Public routes
  {
    path: ROUTES.LOGIN,
    element: <LoginPage />,
  },

  // Protected routes with main layout
  {
    element: (
      <AuthGuard>
        <MainLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: ROUTES.HOME,
        element: <DashboardPage />,
      },
      {
        path: ROUTES.DASHBOARD,
        element: <DashboardPage />,
      },
      {
        path: ROUTES.DELIVERIES,
        element: <DeliveriesListPage />,
      },
      {
        path: ROUTES.DELIVERY_DETAIL,
        element: <DeliveryDetailPage />,
      },
      {
        path: ROUTES.RETURNS,
        element: <ReturnsListPage />,
      },
      {
        path: ROUTES.RETURN_CREATE_FROM_ERP,
        element: <CreateReturnFromErpSalePage />,
      },
      {
        path: ROUTES.RETURN_DETAIL,
        element: <ReturnDetailPage />,
      },
      {
        path: ROUTES.RETURNS_REASON_SUMMARY,
        element: (
          <PermissionGuard roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.OPERATOR, ROLES.SUPERVISOR]}>
            <ReturnReasonSummaryPage />
          </PermissionGuard>
        ),
      },
      {
        path: ROUTES.RETURNS_VERIFICATION,
        element: (
          <PermissionGuard roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.OPERATOR, ROLES.SUPERVISOR]}>
            <VerificationListPage />
          </PermissionGuard>
        ),
      },
      {
        path: ROUTES.RETURNS_VERIFICATION_DETAIL,
        element: (
          <PermissionGuard roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.OPERATOR, ROLES.SUPERVISOR]}>
            <VerificationDetailPage />
          </PermissionGuard>
        ),
      },
      {
        path: ROUTES.RETURNS_ERP_SETTINGS,
        element: (
          <PermissionGuard roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.OPERATOR, ROLES.SUPERVISOR]}>
            <ReturnErpSettingsPage />
          </PermissionGuard>
        ),
      },
      {
        path: ROUTES.MAP_SETTINGS,
        element: (
          <PermissionGuard roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
            <MapSettingsPage />
          </PermissionGuard>
        ),
      },
      {
        path: ROUTES.DELIVERY_REASONS,
        element: (
          <PermissionGuard roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
            <DeliveryReasonsPage />
          </PermissionGuard>
        ),
      },
      {
        path: ROUTES.USERS,
        element: (
          <PermissionGuard roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
            <UsersListPage />
          </PermissionGuard>
        ),
      },
      {
        path: ROUTES.USER_DETAIL,
        element: (
          <PermissionGuard roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
            <UserDetailPage />
          </PermissionGuard>
        ),
      },
      {
        path: ROUTES.ERP_PRODUCTS,
        element: <ErpProductsPage />,
      },
      {
        path: ROUTES.ERP_SALES,
        element: <ErpSalesPage />,
      },
      {
        path: ROUTES.ERP_LOADS,
        element: <ErpLoadsListPage />,
      },
      {
        path: ROUTES.ERP_LOAD_DETAIL,
        element: <ErpLoadDetailPage />,
      },
      {
        // Antes placeholder "Rutas"; ahora el mapa de entregas (solo admin).
        path: ROUTES.ROUTES_PLANNING,
        element: (
          <PermissionGuard roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
            <DeliveryMapPage />
          </PermissionGuard>
        ),
      },
      {
        path: ROUTES.FLEET_DRIVERS,
        element: <DriverSummaryPage />,
      },
      {
        path: ROUTES.FLEET_VEHICLES,
        element: <VehiclesPage />,
      },
      {
        path: ROUTES.CUSTOMERS,
        element: <CustomersPage />,
      },
      {
        path: ROUTES.ERP_SYNC,
        element: <ErpSyncPage />,
      },
      {
        path: ROUTES.ERP_INVENTORY,
        element: <ErpInventoryPage />,
      },
      {
        path: ROUTES.REPORTS,
        element: (
          <PermissionGuard roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.OPERATOR, ROLES.SUPERVISOR]}>
            <ReportsPage />
          </PermissionGuard>
        ),
      },
      {
        // La salida de la bodega solo la autorizan administradores (el backend también lo valida).
        path: ROUTES.WAREHOUSE,
        element: (
          <PermissionGuard roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
            <WarehouseEventsPage />
          </PermissionGuard>
        ),
      },
      {
        path: ROUTES.REPORTS_NOT_RETURNED,
        element: (
          <PermissionGuard roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.OPERATOR, ROLES.SUPERVISOR]}>
            <NotReturnedReportPage />
          </PermissionGuard>
        ),
      },
    ],
  },

  // Driver PWA routes
  {
    element: (
      <AuthGuard>
        <PermissionGuard roles={[ROLES.DRIVER]}>
          <DriverLayout />
        </PermissionGuard>
      </AuthGuard>
    ),
    children: [
      {
        // El conductor solo necesita Cargues y Perfil; "/driver" cae en la misma
        // pantalla que "/driver/loads" (antes había un dashboard separado, roto).
        path: ROUTES.DRIVER,
        element: <MyLoadsPage />,
      },
      {
        path: ROUTES.DRIVER_LOADS,
        element: <MyLoadsPage />,
      },
      {
        path: ROUTES.DRIVER_LOAD_DETAIL,
        element: <LoadDetailPage />,
      },
      {
        path: ROUTES.DRIVER_LOAD_ROUTE,
        element: <DriverRoutePage />,
      },
      {
        path: ROUTES.DRIVER_LOAD_STOP_ACTION,
        element: <StopActionPage />,
      },
      {
        path: ROUTES.DRIVER_PENDING_SYNC,
        element: <DriverPendingSyncPage />,
      },
      {
        path: ROUTES.DRIVER_PROFILE,
        element: <DriverProfilePage />,
      },
      {
        path: ROUTES.DRIVER_NOTIFICATIONS,
        element: <DriverNotificationsPage />,
      },
    ],
  },

  // 404
  {
    path: '*',
    element: <NotFoundPage />,
  },
]
